import { useMsal } from "@azure/msal-react";
import { notifications } from "@mantine/notifications";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DatasourceAccessPendingModal from "src/components/DatasourceAccessPendingModal";
import DatasourceRequestAccessModal from "src/components/DatasourceRequestAccessModal";
import { useDatasources } from "src/hooks/useDatasources";
import { fetchPendingRequests } from "src/hooks/useFetchPendingRequests";
import { useAvailableAlwaysOnDatasourceIds, useUserDatasources } from "src/hooks/useUser";
import { DATASOURCE_BROWSER_ROUTES } from "src/services/datasources/datasourceBrowserRoutes";
import { resolveDatasourceTab } from "src/services/datasources/resolveDatasourceTab";
import {
    type DatasourceKind,
    datasourceKindOfTab,
    visibleDatasources,
} from "src/services/datasources/visibleDatasources";
import { DocumentsRoute } from "./DocumentsRoute";

interface DeniedDatasource {
    id: string;
    name: string;
    description: string;
    ingestion_status: string;
    pending: boolean;
}

export const DatasourceBrowser = ({ kind }: { kind: DatasourceKind }) => {
    const route = DATASOURCE_BROWSER_ROUTES[kind];
    const navigate = useNavigate();
    // biome-ignore lint/suspicious/noExplicitAny: route ids are resolved from a lookup table
    const { tab } = useSearch({ from: route.searchFrom as any }) as { tab?: string };

    const rawUserDatasources = localStorage.getItem("userDatasources") ?? "[]";
    const storedUserDatasources: string[] = useMemo(
        () => JSON.parse(rawUserDatasources),
        [rawUserDatasources],
    );

    const { data: chatVectorIndex = [], isSuccess } = useDatasources();
    const { data: userDatasourceDetails, isSuccess: isUserSuccess } = useUserDatasources();
    const availableAlwaysOnIds = useAvailableAlwaysOnDatasourceIds();
    const { instance } = useMsal();

    const [pendingRequests, setPendingRequests] = useState<Set<string>>(new Set());
    useEffect(() => {
        fetchPendingRequests(instance)
            .then((ids) => {
                if (ids) setPendingRequests(ids);
            })
            .catch(() => {});
    }, [instance]);

    const [deniedDatasource, setDeniedDatasource] = useState<DeniedDatasource | null>(null);
    const [selectedIndex, setSelectedIndex] = useState<string>(
        () => localStorage.getItem(route.storageKey) ?? "",
    );

    const tabBelongsElsewhere = Boolean(tab) && datasourceKindOfTab(tab) !== kind;
    useEffect(() => {
        if (!tabBelongsElsewhere) return;
        const target = DATASOURCE_BROWSER_ROUTES[datasourceKindOfTab(tab)];
        // biome-ignore lint/suspicious/noExplicitAny: generated router types omit search params
        navigate({ to: target.path as any, search: { tab } as any, replace: true });
    }, [tabBelongsElsewhere, tab, navigate]);

    const prevTabRef = useRef<string | undefined>(undefined);
    const hasValidatedRef = useRef(false);

    useEffect(() => {
        if (tabBelongsElsewhere) return;
        if (!isSuccess || !isUserSuccess || chatVectorIndex.length === 0) return;

        const available = visibleDatasources({
            all: chatVectorIndex,
            userRoles: storedUserDatasources ?? [],
            kind,
            requireIndex: route.requireIndex,
            availableAlwaysOnIds,
        });
        const resolution = resolveDatasourceTab({
            tab,
            available,
            all: chatVectorIndex,
            userDatasources: userDatasourceDetails ?? [],
            storedIndex: localStorage.getItem(route.storageKey),
        });
        if (!resolution) return;

        const isTabChange = tab !== prevTabRef.current;
        prevTabRef.current = tab;
        if (!isTabChange && selectedIndex && hasValidatedRef.current) return;
        hasValidatedRef.current = true;

        const { index: resolved } = resolution;
        setSelectedIndex(resolved);
        localStorage.setItem(route.storageKey, resolved);

        setDeniedDatasource(
            resolution.status === "no-access"
                ? {
                      id: resolution.datasourceId,
                      name: resolution.datasourceName,
                      description: resolution.datasourceDescription,
                      ingestion_status: resolution.datasourceIngestionStatus,
                      pending: pendingRequests.has(resolution.datasourceId),
                  }
                : null,
        );

        if (resolution.status === "not-found") {
            notifications.show({
                title: "Data source not found",
                message: "The data source in the link could not be found.",
                color: "orange",
            });
        }

        if (resolution.status !== "no-access" && tab !== resolved) {
            // biome-ignore lint/suspicious/noExplicitAny: generated router types omit search params
            navigate({ to: route.path as any, search: { tab: resolved } as any, replace: true });
        }
    }, [
        tab,
        tabBelongsElsewhere,
        isSuccess,
        isUserSuccess,
        chatVectorIndex,
        userDatasourceDetails,
        storedUserDatasources,
        availableAlwaysOnIds,
        pendingRequests,
        selectedIndex,
        navigate,
        kind,
        route,
    ]);

    const handleSetSelectedIndex = useCallback(
        (value: string) => {
            setSelectedIndex(value);
            localStorage.setItem(route.storageKey, value);
            setTimeout(() => {
                // biome-ignore lint/suspicious/noExplicitAny: generated router types omit search params
                navigate({ to: route.path as any, search: { tab: value } as any });
            });
        },
        [navigate, route],
    );

    const dismissDeniedDatasource = useCallback(() => {
        setDeniedDatasource(null);
        // biome-ignore lint/suspicious/noExplicitAny: generated router types omit search params
        navigate({ to: route.path as any, search: { tab: selectedIndex } as any, replace: true });
    }, [navigate, selectedIndex, route]);

    if (tabBelongsElsewhere || !selectedIndex) return null;

    return (
        <>
            <DocumentsRoute
                kind={kind}
                index={selectedIndex}
                setSelectedIndex={handleSetSelectedIndex}
            />
            {deniedDatasource && !deniedDatasource.pending && (
                <DatasourceRequestAccessModal
                    opened
                    onClose={dismissDeniedDatasource}
                    onSubmit={() => {
                        setPendingRequests((prev) => new Set([...prev, deniedDatasource.id]));
                        notifications.show({
                            title: "Request submitted",
                            message: `Your access request for ${deniedDatasource.name} has been submitted. An admin will review it shortly.`,
                            color: "green",
                        });
                        dismissDeniedDatasource();
                    }}
                    title={`Request Access to ${deniedDatasource.name}`}
                    description={deniedDatasource.description}
                    ingestion_status={deniedDatasource.ingestion_status}
                    requested_datasources={[deniedDatasource.id]}
                />
            )}
            {deniedDatasource?.pending && (
                <DatasourceAccessPendingModal
                    opened
                    onClose={dismissDeniedDatasource}
                    datasourceName={deniedDatasource.name}
                />
            )}
        </>
    );
};
